/**
 * Error taxonomy shared by API and clients. Clients map codes to localized text
 * (`error.<CODE>` in @dijitalapp/i18n); they never show raw server messages to users.
 */
export const ERROR_CODES = [
  'AUTH_INVALID_CREDENTIALS',
  'AUTH_SESSION_EXPIRED',
  'AUTH_REQUIRED',
  'AUTH_ORIGIN_REJECTED',
  'AGE_BELOW_MINIMUM',
  'EMAIL_TAKEN',
  'USERNAME_TAKEN',
  'USERNAME_RESERVED',
  'USERNAME_NOT_ALLOWED',
  'SITE_CHANGED',
  'USERNAME_CHANGE_TOO_SOON',
  'VALIDATION_FAILED',
  'RATE_LIMITED',
  'FORBIDDEN',
  'NOT_FOUND',
  'POLICY_LOCKED',
  'SPACE_VERIFICATION_REQUIRED',
  'TERMS_NOT_ACCEPTED',
  'ENTITLEMENT_LIMIT',
  'EMAIL_TOKEN_INVALID',
  'PASSKEY_FAILED',
  'EMAIL_NOT_VERIFIED',
  /** Recipient blocked the sender, or their privacy settings do not accept the sender. Deliberately not distinguished. */
  'MESSAGING_NOT_ALLOWED',
  'MESSAGE_TOO_LARGE',
  'AUTH_TICKET_INVALID',
  'PROTOCOL_INVALID_FRAME',
  'REGISTRATION_CLOSED',
  'INVITE_INVALID',
  'ADMIN_PASSKEY_REQUIRED',
  'MESSAGE_EDIT_EXPIRED',
  'ATTACHMENT_REJECTED',
  'STORAGE_QUOTA_EXCEEDED',
  'GROUP_LINK_INVALID',
  'GROUP_FULL',
  'SPACE_LINK_INVALID',
  'EDU_INVITE_INVALID',
  'ATTACHMENT_MALWARE',
  'ATTACHMENT_SCAN_UNAVAILABLE',
  'PIN_LIMIT',
  'E2EE_REQUIRED',
  'E2EE_UNAVAILABLE',
  'E2EE_UNSUPPORTED',
  'AI_UNAVAILABLE',
  'AI_CONSENT_REQUIRED',
  'AI_QUOTA_EXCEEDED',
  'AI_REFUSED',
  'UNKNOWN',
] as const;

export type ErrorCode = (typeof ERROR_CODES)[number];

export interface ApiErrorBody {
  error: {
    code: ErrorCode;
    /** Developer-facing detail. Never contains secrets or other users' data. */
    message: string;
    requestId?: string;
    fields?: Record<string, string>;
  };
}

export const ERROR_HTTP_STATUS: Record<ErrorCode, number> = {
  AUTH_INVALID_CREDENTIALS: 401,
  AUTH_SESSION_EXPIRED: 401,
  AUTH_REQUIRED: 401,
  AUTH_ORIGIN_REJECTED: 403,
  AGE_BELOW_MINIMUM: 403,
  EMAIL_TAKEN: 409,
  USERNAME_TAKEN: 409,
  USERNAME_RESERVED: 409,
  USERNAME_NOT_ALLOWED: 422,
  SITE_CHANGED: 409,
  USERNAME_CHANGE_TOO_SOON: 429,
  VALIDATION_FAILED: 400,
  RATE_LIMITED: 429,
  FORBIDDEN: 403,
  NOT_FOUND: 404,
  POLICY_LOCKED: 403,
  SPACE_VERIFICATION_REQUIRED: 403,
  TERMS_NOT_ACCEPTED: 400,
  ENTITLEMENT_LIMIT: 403,
  EMAIL_TOKEN_INVALID: 400,
  PASSKEY_FAILED: 400,
  EMAIL_NOT_VERIFIED: 403,
  MESSAGING_NOT_ALLOWED: 403,
  MESSAGE_TOO_LARGE: 413,
  AUTH_TICKET_INVALID: 401,
  PROTOCOL_INVALID_FRAME: 400,
  REGISTRATION_CLOSED: 403,
  INVITE_INVALID: 403,
  ADMIN_PASSKEY_REQUIRED: 403,
  MESSAGE_EDIT_EXPIRED: 403,
  ATTACHMENT_REJECTED: 415,
  STORAGE_QUOTA_EXCEEDED: 403,
  GROUP_LINK_INVALID: 404,
  GROUP_FULL: 409,
  SPACE_LINK_INVALID: 404,
  EDU_INVITE_INVALID: 404,
  ATTACHMENT_MALWARE: 422,
  ATTACHMENT_SCAN_UNAVAILABLE: 503,
  PIN_LIMIT: 409,
  E2EE_REQUIRED: 409,
  E2EE_UNAVAILABLE: 409,
  E2EE_UNSUPPORTED: 409,
  AI_UNAVAILABLE: 503,
  AI_CONSENT_REQUIRED: 403,
  AI_QUOTA_EXCEEDED: 429,
  AI_REFUSED: 422,
  UNKNOWN: 500,
};
